
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { UserRole, NewsItem } from '../types';
import { Navigate } from 'react-router-dom';
import { FileText, Copy, RefreshCw, Link as LinkIcon, Edit2, CheckCircle, Save, X, Download } from 'lucide-react';
import { apiRequest } from '../utils/api';

const downloadAsText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export const AdminContext: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'CONTEXT' | 'NEWS' | 'RACE_SUMMARY' | 'POLLS'>('CONTEXT');
    
    // Context State
    const [contextText, setContextText] = useState('');
    const [loadingContext, setLoadingContext] = useState(false);
    
    // Race Summary State
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [raceSummaryText, setRaceSummaryText] = useState('');
    const [loadingSummary, setLoadingSummary] = useState(false);
    
    // News State
    const [newsList, setNewsList] = useState<NewsItem[]>([]);
    const [pollsList, setPollsList] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [loadingNews, setLoadingNews] = useState(false);
    const [editingNewsId, setEditingNewsId] = useState<number | null>(null);
    const [editingPollId, setEditingPollId] = useState<number | null>(null);
    const [editEventId, setEditEventId] = useState<string>('');

    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    // --- RACE SUMMARY GENERATOR ---
    const generateRaceSummary = async () => {
        if (!selectedEventId) return;
        setLoadingSummary(true);
        setRaceSummaryText('');
        try {
            const res = await apiRequest<string>(`race_summary.php?eventId=${selectedEventId}`);
            if (typeof res === 'string') {
                setRaceSummaryText(res);
            } else {
                setRaceSummaryText(JSON.stringify(res, null, 2));
            }
        } catch(e: any) {
            setRaceSummaryText("Fehler beim Laden: " + e.message);
        } finally {
            setLoadingSummary(false);
        }
    };

    // --- CONTEXT GENERATOR ---
    const generateContext = async () => {
        setLoadingContext(true);
        setContextText('');
        try {
            const res = await apiRequest<string>('admin_context.php');
            
            if (typeof res === 'string') {
                setContextText(res);
            } else {
                console.warn("Unexpected response format", res);
                setContextText(JSON.stringify(res, null, 2));
            }
        } catch(e: any) {
            setContextText("Fehler beim Laden: " + e.message);
        } finally {
            setLoadingContext(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        alert("In die Zwischenablage kopiert!");
    };

    // --- NEWS & POLLS MANAGER ---
    const loadNewsData = async () => {
        setLoadingNews(true);
        try {
            // Load each independently so one failure doesn't break all
            const [nData, pData, eData] = await Promise.all([
                apiRequest<NewsItem[]>(`news.php?limit=50&userId=${user.id}`).catch(() => []),
                apiRequest<any[]>(`polls.php?admin=true&userId=${user.id}`).catch(() => []),
                apiRequest<any[]>('events.php?scope=recent').catch(() => [])
            ]);
            setNewsList(Array.isArray(nData) ? nData : []);
            setPollsList(Array.isArray(pData) ? pData : []);
            const sortedEvents = Array.isArray(eData) ? [...eData].sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];
            setEvents(sortedEvents);
            if (sortedEvents.length > 0 && !selectedEventId) {
                setSelectedEventId(sortedEvents[0].id);
            }
        } catch(e) { console.error(e); }
        finally { setLoadingNews(false); }
    };

    useEffect(() => {
        if (activeTab === 'CONTEXT') generateContext();
        if (activeTab === 'NEWS' || activeTab === 'RACE_SUMMARY' || activeTab === 'POLLS') loadNewsData();
    }, [activeTab]);

    const startEditNews = (n: NewsItem) => {
        setEditingNewsId(n.id);
        setEditingPollId(null);
        // @ts-ignore
        setEditEventId(n.event_id || '');
    };

    const startEditPoll = (p: any) => {
        setEditingPollId(p.id);
        setEditingNewsId(null);
        setEditEventId(p.event_id || '');
    };

    const saveNewsEvent = async (id: number) => {
        try {
            await apiRequest('news.php', 'PUT', {
                id: id,
                event_id: editEventId || null
            });
            setEditingNewsId(null);
            loadNewsData(); // Refresh list to show updated link
        } catch(e: any) {
            alert(e.message);
        }
    };

    const savePollEvent = async (id: number) => {
        try {
            await apiRequest('polls.php', 'PUT', {
                id: id,
                eventId: editEventId || null
            });
            setEditingPollId(null);
            loadNewsData(); // Refresh list to show updated link
        } catch(e: any) {
            alert(e.message);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                        <FileText size={32}/> LLM Context Generator
                    </h1>
                    <p className="text-gray-400 text-sm">Saison-Daten für KI-Artikel aufbereiten</p>
                </div>
                
                <div className="flex bg-gray-800 rounded p-1">
                    <button onClick={() => setActiveTab('CONTEXT')} className={`px-4 py-2 rounded text-xs font-bold uppercase ${activeTab === 'CONTEXT' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>Saison Generator</button>
                    <button onClick={() => setActiveTab('RACE_SUMMARY')} className={`px-4 py-2 rounded text-xs font-bold uppercase ${activeTab === 'RACE_SUMMARY' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>Renn-Zusammenfassung</button>
                    <button onClick={() => setActiveTab('NEWS')} className={`px-4 py-2 rounded text-xs font-bold uppercase ${activeTab === 'NEWS' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>News Linking</button>
                    <button onClick={() => setActiveTab('POLLS')} className={`px-4 py-2 rounded text-xs font-bold uppercase ${activeTab === 'POLLS' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>Poll Linking</button>
                </div>
            </div>

            {activeTab === 'CONTEXT' && (
                <div className="space-y-4">
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={generateContext} disabled={loadingContext} icon={RefreshCw}>Neu generieren</Button>
                        <Button variant="secondary" onClick={() => downloadAsText(contextText, 'saison_context.txt')} disabled={!contextText} icon={Download}>Download .txt</Button>
                        <Button onClick={() => copyToClipboard(contextText)} disabled={!contextText} icon={Copy}>Kopieren</Button>
                    </div>
                    
                    <Card className="bg-[#1e1e24] border-gray-700 p-0 overflow-hidden">
                        <div className="p-2 bg-gray-900 border-b border-gray-800 text-xs text-gray-500 font-mono">
                            Markdown Format • Automatisch generiert aus DB (Gesamte Saison)
                        </div>
                        {loadingContext ? (
                            <div className="p-20 text-center text-gray-500 flex flex-col items-center gap-2">
                                <RefreshCw className="animate-spin"/>
                                Berechne Saisonverlauf...
                            </div>
                        ) : (
                            <textarea 
                                className="w-full h-[600px] bg-[#0f0f13] text-gray-300 font-mono text-xs p-4 focus:outline-none resize-none"
                                value={contextText}
                                readOnly
                                placeholder="Klicke auf 'Neu generieren' um die Daten zu laden..."
                            />
                        )}
                    </Card>
                </div>
            )}

            {activeTab === 'RACE_SUMMARY' && (
                <div className="space-y-4">
                    <Card className="bg-[#1e1e24] border-gray-700">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Event auswählen</label>
                                <select 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm"
                                    value={selectedEventId}
                                    onChange={e => setSelectedEventId(e.target.value)}
                                >
                                    {events.map(e => (
                                        <option key={e.id} value={e.id}>{e.date} - {e.trackLocation} (R{e.roundNumber})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="secondary" onClick={generateRaceSummary} disabled={loadingSummary || !selectedEventId} icon={RefreshCw}>Zusammenfassung erstellen</Button>
                                <Button variant="secondary" onClick={() => {
                                    const evt = events.find(e => e.id === selectedEventId);
                                    downloadAsText(raceSummaryText, `renn_zusammenfassung_${evt?.trackLocation || 'event'}.txt`);
                                }} disabled={!raceSummaryText} icon={Download}>Download .txt</Button>
                                <Button onClick={() => copyToClipboard(raceSummaryText)} disabled={!raceSummaryText} icon={Copy}>Kopieren</Button>
                            </div>
                        </div>
                    </Card>

                    <Card className="bg-[#1e1e24] border-gray-700 p-0 overflow-hidden">
                        <div className="p-2 bg-gray-900 border-b border-gray-800 text-xs text-gray-500 font-mono">
                            Markdown Format • Inkl. Telemetrie-JSON Daten
                        </div>
                        {loadingSummary ? (
                            <div className="p-20 text-center text-gray-500 flex flex-col items-center gap-2">
                                <RefreshCw className="animate-spin"/>
                                Analysiere Renndaten...
                            </div>
                        ) : (
                            <textarea 
                                className="w-full h-[600px] bg-[#0f0f13] text-gray-300 font-mono text-xs p-4 focus:outline-none resize-none"
                                value={raceSummaryText}
                                readOnly
                                placeholder="Wähle ein Event und klicke auf 'Zusammenfassung erstellen'..."
                            />
                        )}
                    </Card>
                </div>
            )}

            {activeTab === 'NEWS' && (
                <Card title="News mit Rennen verknüpfen">
                    <p className="text-sm text-gray-400 mb-4">
                        Ordne News-Artikel (Vorberichte, Rennberichte) den entsprechenden Events zu. 
                        Dies hilft der KI, den narrativen Kontext (Headlines) zum richtigen Zeitpunkt in den Saisonverlauf einzubauen.
                    </p>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 text-xs font-bold uppercase">
                                <tr>
                                    <th className="p-3">Datum</th>
                                    <th className="p-3">Titel</th>
                                    <th className="p-3">Kategorie</th>
                                    <th className="p-3">Verknüpftes Event</th>
                                    <th className="p-3 text-right">Aktion</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {newsList.map(n => (
                                    <tr key={n.id} className="hover:bg-gray-800/50">
                                        <td className="p-3 text-gray-500 text-xs">{new Date(n.created_at).toLocaleDateString()}</td>
                                        <td className="p-3 font-bold text-white truncate max-w-xs">{n.title}</td>
                                        <td className="p-3"><span className="text-xs bg-gray-800 px-2 py-0.5 rounded text-gray-300">{n.category}</span></td>
                                        
                                        <td className="p-3">
                                            {editingNewsId === n.id ? (
                                                <select 
                                                    className="bg-gray-900 border border-gray-600 rounded p-1 text-xs text-white w-full"
                                                    value={editEventId}
                                                    onChange={e => setEditEventId(e.target.value)}
                                                >
                                                    <option value="">-- Kein Event --</option>
                                                    {events.map(e => (
                                                        <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                // @ts-ignore
                                                n.track_location ? (
                                                    <span className="text-yellow-500 text-xs font-bold flex items-center gap-1">
                                                        <LinkIcon size={10}/> 
                                                        {/* @ts-ignore */}
                                                        {n.track_location}
                                                    </span>
                                                ) : <span className="text-gray-600 text-xs">-</span>
                                            )}
                                        </td>
                                        
                                        <td className="p-3 text-right">
                                            {editingNewsId === n.id ? (
                                                <div className="flex justify-end gap-1">
                                                    <button onClick={() => saveNewsEvent(n.id)} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-500"><Save size={14}/></button>
                                                    <button onClick={() => setEditingNewsId(null)} className="p-1.5 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"><X size={14}/></button>
                                                </div>
                                            ) : (
                                                <button onClick={() => startEditNews(n)} className="p-1.5 bg-gray-800 text-blue-400 rounded hover:bg-gray-700 border border-gray-700">
                                                    <Edit2 size={14}/>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
            {activeTab === 'POLLS' && (
                <Card title="Umfragen mit Rennen verknüpfen">
                    <p className="text-sm text-gray-400 mb-4">
                        Verknüpfe Community-Umfragen mit spezifischen Rennen. 
                        Diese erscheinen dann direkt in der Rennzusammenfassung und im Event-Detail Tab.
                    </p>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 text-xs font-bold uppercase">
                                <tr>
                                    <th className="p-3">Datum</th>
                                    <th className="p-3">Frage</th>
                                    <th className="p-3">Status</th>
                                    <th className="p-3">Verknüpftes Event</th>
                                    <th className="p-3 text-right">Aktion</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {pollsList.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-800/50">
                                        <td className="p-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                                        <td className="p-3 font-bold text-white truncate max-w-xs">{p.question}</td>
                                        <td className="p-3"><Badge color={p.status === 'OPEN' ? 'green' : 'gray'}>{p.status}</Badge></td>
                                        
                                        <td className="p-3">
                                            {editingPollId === p.id ? (
                                                <select 
                                                    className="bg-gray-900 border border-gray-600 rounded p-1 text-xs text-white w-full"
                                                    value={editEventId}
                                                    onChange={e => setEditEventId(e.target.value)}
                                                >
                                                    <option value="">-- Kein Event --</option>
                                                    {events.map(e => (
                                                        <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                p.track_location ? (
                                                    <span className="text-yellow-500 text-xs font-bold flex items-center gap-1">
                                                        <LinkIcon size={10}/> 
                                                        {p.track_location}
                                                    </span>
                                                ) : <span className="text-gray-600 text-xs">-</span>
                                            )}
                                        </td>
                                        
                                        <td className="p-3 text-right">
                                            {editingPollId === p.id ? (
                                                <div className="flex justify-end gap-1">
                                                    <button onClick={() => savePollEvent(p.id)} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-500"><Save size={14}/></button>
                                                    <button onClick={() => setEditingPollId(null)} className="p-1.5 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"><X size={14}/></button>
                                                </div>
                                            ) : (
                                                <button onClick={() => startEditPoll(p)} className="p-1.5 bg-gray-800 text-blue-400 rounded hover:bg-gray-700 border border-gray-700">
                                                    <Edit2 size={14}/>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
};
